import { Grid } from "@mui/material";
import { useAudienceAnalyticsContext } from "Providers/AudienceAnalyticsProvider";
import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { useMemo } from "react";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { AnalyticsChartStatCard } from "features/analytics";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import {
  CHART_KEY_AUDIENCE_USER_JOURNEY_AUDIENCE_COMPOSITION,
  CHART_KEY_AUDIENCE_USER_JOURNEY_CONVERSION_JOURNEY_CHANNEL,
  CHART_KEY_AUDIENCE_USER_JOURNEY_CONVERSION_JOURNEY_PAGEVIEW,
  CHART_KEY_AUDIENCE_USER_JOURNEY_DAYS_PRIOR_CONVERSIONS,
  CHART_KEY_AUDIENCE_USER_JOURNEY_SESSIONS_PRIOR_CONVERSIONS,
  CHART_KEY_AUDIENCE_USER_JOURNEY_TOP_DIMENSION_TREND,
} from "features/analytics/constants/chartKeys";

interface IAudienceUserJourneyReportPageProps {
  chartKeyScope: string;
  dimension: ApiDimension;
  countryName?: string;
  channelName?: string;
  userType?: string | "New" | "Returning";
  device?: string;
  campaign?: string;
}

function AudienceUserJourneyReportPage({
  chartKeyScope,
  dimension,
  countryName,
  channelName,
  userType,
  device,
  campaign,
}: IAudienceUserJourneyReportPageProps) {
  const { currentAudience } = useAudienceAnalyticsContext();
  const dimensionToName = useDimensionName();

  const attributes = useMemo(() => {
    let filter: ApiChartAttribute[] = [];
    if (currentAudience && currentAudience !== "") {
      filter.push({
        key: ApiDimension.DIMENSION_LEAD_AUDIENCE,
        value: currentAudience,
      });
    }
    if (countryName && countryName !== "") {
      filter.push({
        key: ApiDimension.DIMENSION_COUNTRY,
        value: countryName,
      });
    }
    if (channelName && channelName !== "") {
      filter.push({
        key: ApiDimension.DIMENSION_CHANNEL,
        value: channelName,
      });
    }
    if (userType && userType !== "") {
      filter.push({
        key: ApiDimension.DIMENSION_VISITOR_TYPE,
        value: userType,
      });
    }
    if (campaign && campaign !== "") {
      filter.push({
        key: ApiDimension.DIMENSION_CAMPAIGN,
        value: campaign,
      });
    }
    if (device && device !== "") {
      filter.push({
        key: ApiDimension.DIMENSION_DEVICE,
        value: device,
      });
    }

    return filter;
  }, [currentAudience, countryName, device, channelName, userType, campaign]);

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={3} container spacing={2}>
        <Grid item xs={12}>
          <AnalyticsChartStatCard
            hideTrendedSpark
            noToolbar
            forceComparison
            chart={{
              name: "",
              dimension: [],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_LEADS_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "Total Leads",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ minHeight: 340 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_AUDIENCE_USER_JOURNEY_AUDIENCE_COMPOSITION}
            chart={{
              name: `Recorded Audience Composition`,
              dimension: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: attributes.filter(
                (a) => a.key !== ApiDimension.DIMENSION_LEAD_AUDIENCE,
              ),
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_LEADS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                showTotals: true,
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={9}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_AUDIENCE_USER_JOURNEY_TOP_DIMENSION_TREND}_${chartKeyScope}`}
          chart={{
            name: `Top ${
              dimension !== ApiDimension.DIMENSION_NOT_SET
                ? dimensionToName(dimension)
                : ""
            } Trend`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY, dimension],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: attributes.filter(
              (a) => a.key === ApiDimension.DIMENSION_LEAD_AUDIENCE,
            ),
            limit: 5,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={5} sx={{ minHeight: 468 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_USER_JOURNEY_CONVERSION_JOURNEY_CHANNEL}
          chart={{
            name: `Conversion Journey on the Channel Level`,
            dimension: [
              ApiDimension.DIMENSION_CHANNEL_MACRO_CONVERSION_JOURNEY,
            ],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={7}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_USER_JOURNEY_CONVERSION_JOURNEY_PAGEVIEW}
          chart={{
            name: `Conversion Journey on the Pageview Level`,
            dimension: [ApiDimension.DIMENSION_CONVERTING_SESSION_JOURNEY],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 350 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_USER_JOURNEY_SESSIONS_PRIOR_CONVERSIONS}
          chart={{
            name: `No. of Sessions Prior Conversions`,
            dimension: [
              ApiDimension.DIMENSION_VISITS_SINCE_PRIOR_MACRO_CONVERSION,
            ],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes,
            orderBy: `${ApiDimension.DIMENSION_VISITS_SINCE_PRIOR_MACRO_CONVERSION}`,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]:
                    ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 350 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_USER_JOURNEY_DAYS_PRIOR_CONVERSIONS}
          chart={{
            name: `No. of Days Prior Conversions`,
            dimension: [
              ApiDimension.DIMENSION_DAYS_SINCE_PRIOR_MACRO_CONVERSION,
            ],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes,
            orderBy: `${ApiDimension.DIMENSION_DAYS_SINCE_PRIOR_MACRO_CONVERSION}`,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]:
                    ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default AudienceUserJourneyReportPage;
